import './Dropzone.scss';

import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { Accept, useDropzone } from 'react-dropzone';
import { Trans, useTranslation } from 'react-i18next';

import FileUploaded from '@components/FileUploaded';
import { FileUploadStatus } from '@modulate/shared/src/constants/file-upload-status';
import { IFileUploaded } from '@modulate/shared/src/models/file';
import { usePatientId } from '@src/hooks/get-id';

export interface IDropzoneProps {
  id?: string;
  files?: IFileUploaded[];
  upload: (file: File, patientId: string) => Promise<IFileUploaded>;
  onChange?: (files: IFileUploaded[], isUpdating: boolean) => void;
  deleteFile?: (file: IFileUploaded) => Promise<void>;
  viewFile?: (file: IFileUploaded) => Promise<void>;
  children?: ReactNode | ReactNode[];
  accept?: Accept;
  maxFiles?: number;
  multiple?: boolean;
  disabled?: boolean;
}

const Dropzone: React.FC<IDropzoneProps> = ({
  id,
  upload,
  onChange,
  deleteFile,
  viewFile,
  children,
  files: initialFiles = [],
  ...props
}) => {
  const { t } = useTranslation();
  const patientId = usePatientId();

  const [isUploading, setIsUploading] = useState(false);
  const [files, setFiles] = useState<IFileUploaded[]>(
    initialFiles.map((file) => ({ ...file, status: FileUploadStatus.Completed })),
  );

  useEffect(() => {
    onChange?.(files, isUploading);
  }, [files, isUploading]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const currentFiles: IFileUploaded[] = acceptedFiles.map((file) => ({
      name: file.name,
      type: file.type,
      url: '',
      status: FileUploadStatus.Uploading,
    }));

    setIsUploading(true);
    setFiles((files) => [...files, ...currentFiles]);
    (await Promise.allSettled(acceptedFiles.map((file) => upload(file, patientId)))).map((result, i) => {
      currentFiles[i].id = result.status === 'fulfilled' && String(i);
      currentFiles[i].url = result.status === 'fulfilled' && result.value.url;
      console.log(result);
      currentFiles[i].status =
        result.status === 'fulfilled' ? FileUploadStatus.Completed : FileUploadStatus.Faulty;
    });
    console.log(currentFiles);

    setIsUploading(false);
  }, []);

  const onDelete = async (file: IFileUploaded, index: number) => {
    if (file.status !== FileUploadStatus.Completed || confirm(t('dropzone.confirm'))) {
      setFiles((files) => files.filter((_, i) => i !== index));
      await deleteFile?.(file);
    }
  };

  const isDisabled =
    props.disabled || isUploading || (props.maxFiles !== undefined && files.length >= props.maxFiles);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    ...props,
    disabled: isDisabled,
  });

  const className = classNames('modDropzone__zone', {
    'modDropzone__zone--active': isDragActive,
    'modDropzone__zone--disabled': isDisabled,
  });
  return (
    <div className="modDropzone">
      <div className={className} {...getRootProps()}>
        <input id={id} {...getInputProps()} />
        <p className="modText modText--body1">
          <Trans
            i18nKey="dropzone.instruction"
            components={{ underline: <span className="modText modText--underline" /> }}
          />
        </p>
        {children && (
          <p className="modText modText--body2 modText--secondary modText--centered">{children}</p>
        )}
      </div>
      {files.length > 0 && (
        <ul className="modDropzone__filelist">
          {files.map((file, i) => (
            <li className="modDropzone__filelist__file" key={i}>
              <FileUploaded
                file={file}
                onViewClicked={() => viewFile?.(file)}
                onDeleteClicked={() => onDelete(file, i)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Dropzone;
